import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Trash2, MessageCircle, Heart, Coffee, Reply } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("ro-RO", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return "";
  }
};

const CommentRow = ({ c, onApprove, onDelete, onReply, pending }) => {
  const [showReply, setShowReply] = useState(false);
  const [draft, setDraft] = useState(c.reply || "");
  const [savingReply, setSavingReply] = useState(false);

  const saveReply = async () => {
    setSavingReply(true);
    await onReply(c.id, draft.trim());
    setSavingReply(false);
    setShowReply(false);
  };

  return (
    <li
      data-testid={`mod-comment-${c.id}`}
      className={`rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] p-5 ${pending ? "bg-[color-mix(in_srgb,var(--tint-butter)_60%,transparent)]" : "bg-[#2B1B13]"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-serif text-lg font-medium text-[#F7F1E8]">{c.author_name}</p>
          <p className="mt-0.5 text-[11px] uppercase tracking-[0.2em] text-[#F7F1E8]/62">
            {fmt(c.created_at)} · la{" "}
            <Link to={`/post/${c.post_slug}`} className="text-[var(--accent)] hover:underline" target="_blank" rel="noopener noreferrer">
              {c.post_title}
            </Link>
          </p>
        </div>
        <div className="flex items-center gap-2">
          {pending && (
            <button
              data-testid={`mod-approve-${c.id}`}
              onClick={() => onApprove(c.id)}
              className="inline-flex items-center gap-1.5 rounded-full bg-[var(--teal)] px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-white transition-colors hover:bg-[var(--teal-dark)]"
            >
              <Check className="h-3.5 w-3.5" strokeWidth={2} /> Aprobă
            </button>
          )}
          <button
            data-testid={`mod-reply-toggle-${c.id}`}
            onClick={() => setShowReply((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-full bg-[var(--tint-pink)] px-4 py-2 text-xs font-medium uppercase tracking-[0.12em] text-[var(--accent-dark)] transition-colors hover:bg-[var(--accent)] hover:text-white"
          >
            <Reply className="h-3.5 w-3.5" strokeWidth={2} /> {c.reply ? "Editează răspunsul" : "Răspunde"}
          </button>
          <button
            data-testid={`mod-delete-${c.id}`}
            onClick={() => onDelete(c.id)}
            className="rounded-full p-2 text-[#F7F1E8]/68 transition-colors hover:bg-red-50 hover:text-red-500"
            aria-label="Șterge nota"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.6} />
          </button>
        </div>
      </div>
      <p className="mt-3 whitespace-pre-line text-[14px] leading-relaxed text-[#F7F1E8]/90">{c.body}</p>

      {c.reply && !showReply && (
        <div className="mt-3 rounded-xl bg-[color-mix(in_srgb,var(--tint-pink)_60%,transparent)] px-4 py-3" data-testid={`mod-reply-shown-${c.id}`}>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--accent-dark)]">Răspunsul tău</p>
          <p className="mt-1 whitespace-pre-line text-[13px] leading-relaxed text-[#F7F1E8]/90">{c.reply}</p>
        </div>
      )}

      {showReply && (
        <div className="mt-3 space-y-2" data-testid={`mod-reply-form-${c.id}`}>
          <textarea
            data-testid={`mod-reply-input-${c.id}`}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder={`Răspunde-i lui ${c.author_name}…`}
            className="w-full rounded-xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13] px-4 py-3 text-sm focus:border-[var(--accent)] focus:outline-none focus:ring-2 focus:ring-[var(--tint-pink)]"
          />
          <div className="flex items-center gap-2">
            <button
              data-testid={`mod-reply-save-${c.id}`}
              onClick={saveReply}
              disabled={savingReply}
              className="rounded-full bg-[var(--accent)] px-5 py-2 text-xs font-medium uppercase tracking-[0.12em] text-white transition-colors hover:bg-[var(--accent-dark)] disabled:opacity-60"
            >
              {savingReply ? "Se salvează…" : c.reply && !draft.trim() ? "Șterge răspunsul" : "Publică răspunsul"}
            </button>
            <button
              onClick={() => {
                setShowReply(false);
                setDraft(c.reply || "");
              }}
              className="text-xs uppercase tracking-[0.15em] text-[#F7F1E8]/68 hover:text-[#F7F1E8]"
            >
              Renunță
            </button>
          </div>
          <p className="text-[11px] text-[#F7F1E8]/62">Răspunsul apare public sub notă, dacă nota este aprobată.</p>
        </div>
      )}
    </li>
  );
};

export const AdminCommentsPage = () => {
  const [comments, setComments] = useState([]);
  const [reactions, setReactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/comments")
      .then((r) => setComments(r.data))
      .catch((e) => toast.error(formatApiError(e, "Nu am putut încărca notele")))
      .finally(() => setLoading(false));
    api
      .get("/admin/reactions")
      .then((r) => setReactions(r.data))
      .catch(() => {});
  }, []);

  const approve = async (id) => {
    try {
      await api.put(`/admin/comments/${id}/approve`);
      setComments((all) => all.map((c) => (c.id === id ? { ...c, approved: true } : c)));
      toast.success("Nota este acum publică.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut aproba nota"));
    }
  };

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Ștergi această notă? Acțiunea nu poate fi anulată.")) return;
    try {
      await api.delete(`/admin/comments/${id}`);
      setComments((all) => all.filter((c) => c.id !== id));
      toast.success("Notă ștearsă");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut șterge nota"));
    }
  };

  const reply = async (id, text) => {
    try {
      const { data } = await api.put(`/admin/comments/${id}/reply`, { reply: text });
      setComments((all) => all.map((c) => (c.id === id ? { ...c, reply: data.reply, reply_at: data.reply_at } : c)));
      toast.success(text ? "Răspunsul a fost publicat." : "Răspunsul a fost șters.");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut salva răspunsul"));
    }
  };

  const removeReaction = async (id) => {
    try {
      await api.delete(`/admin/reactions/${id}`);
      setReactions((all) => all.filter((r) => r.id !== id));
      toast.success("Apreciere ștearsă");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut șterge aprecierea"));
    }
  };

  const pending = comments.filter((c) => !c.approved);
  const approved = comments.filter((c) => c.approved);

  return (
    <main data-testid="admin-comments" className="mx-auto max-w-4xl px-6 py-12">
      <Link
        to="/admin"
        data-testid="comments-back-link"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>

      <div className="mt-8 border-b border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Moderare</p>
        <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Notele cititorilor</h1>
        <p className="mt-2 text-sm text-[#F7F1E8]/78">
          Notele apar public doar după ce le aprobi. {pending.length}{" "}
          {pending.length === 1 ? "notă așteaptă" : "note așteaptă"} aprobarea ta.
        </p>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68" data-testid="comments-loading">
          Adun notele&hellip;
        </p>
      ) : comments.length === 0 ? (
        <div className="mt-10 rounded-3xl bg-[var(--tint-mint)] p-10 text-center" data-testid="comments-none">
          <MessageCircle className="mx-auto h-8 w-8 text-[var(--teal)]" strokeWidth={1.4} />
          <p className="mt-4 font-serif text-2xl text-[#F7F1E8]">Nicio notă încă.</p>
          <p className="mt-2 text-sm text-[#F7F1E8]/78">Când cititorii lasă note, ele apar mai întâi aici.</p>
        </div>
      ) : (
        <>
          <section className="mt-10">
            <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-[var(--sun-dark)]">
              În așteptare ({pending.length})
            </h2>
            {pending.length === 0 ? (
              <p className="mt-4 text-sm text-[#F7F1E8]/68" data-testid="no-pending">
                Nimic de aprobat. Frumos.
              </p>
            ) : (
              <ul className="mt-4 space-y-4" data-testid="pending-list">
                {pending.map((c) => (
                  <CommentRow key={c.id} c={c} pending onApprove={approve} onDelete={remove} onReply={reply} />
                ))}
              </ul>
            )}
          </section>

          <section className="mt-12">
            <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-[var(--teal-dark)]">
              Aprobate ({approved.length})
            </h2>
            {approved.length === 0 ? (
              <p className="mt-4 text-sm text-[#F7F1E8]/68">Încă nicio notă aprobată.</p>
            ) : (
              <ul className="mt-4 space-y-4" data-testid="approved-list">
                {approved.map((c) => (
                  <CommentRow key={c.id} c={c} pending={false} onApprove={approve} onDelete={remove} onReply={reply} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}

      <section className="mt-12" data-testid="named-reactions-section">
        <h2 className="text-xs font-medium uppercase tracking-[0.25em] text-[#E6414F]">
          Aprecieri cu nume ({reactions.length})
        </h2>
        {reactions.length === 0 ? (
          <p className="mt-4 text-sm text-[#F7F1E8]/68">
            Când un cititor apasă o inimă sau o cafea și își lasă numele, apare aici.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-[color-mix(in_srgb,var(--milk)_18%,transparent)] rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13]">
            {reactions.map((r) => (
              <li key={r.id} data-testid={`named-reaction-${r.id}`} className="flex items-center gap-3 px-5 py-3">
                {r.type === "heart" ? (
                  <Heart className="h-4 w-4 shrink-0 text-[var(--accent)]" fill="#B5121B" strokeWidth={1.5} />
                ) : (
                  <Coffee className="h-4 w-4 shrink-0 text-[var(--sun)]" strokeWidth={1.8} />
                )}
                <span className="text-sm font-medium text-[#F7F1E8]">{r.name}</span>
                <span className="min-w-0 flex-1 truncate text-xs text-[#F7F1E8]/68">la „{r.post_title}" · {fmt(r.created_at)}</span>
                <button
                  data-testid={`named-reaction-delete-${r.id}`}
                  onClick={() => removeReaction(r.id)}
                  className="rounded-full p-1.5 text-[#F7F1E8]/58 transition-colors hover:bg-red-50 hover:text-red-500"
                  aria-label="Șterge aprecierea"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.6} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
};

export default AdminCommentsPage;
